import { useEffect, useState, useRef } from "react";
import { adminApi, formatApiError } from "@/admin/api";
import PageHeader, { Btn } from "@/admin/components/PageHeader";
import { toast } from "sonner";
import { MagnifyingGlass, UploadSimple, Sparkle, Trash, ArrowClockwise } from "@phosphor-icons/react";

export default function LeadsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [states, setStates] = useState([]);
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/leads", {
        params: { q, state, source, status, skip, limit: 50 },
      });
      setRows(data.items);
      setTotal(data.total);
      setStates(data.states || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [state, source, status, skip]);

  const search = (e) => {
    e.preventDefault();
    setSkip(0);
    load();
  };

  const yelpSearch = async () => {
    const term = prompt("Yelp search term (e.g., 'land surveyor')", "land surveyor");
    if (!term) return;
    const per = Number(prompt("Results per state (1–50)", "10")) || 10;
    toast.loading("Yelp searching across 50 states… may take 30–60s", { id: "yelp" });
    try {
      const { data } = await adminApi.post("/leads/yelp-search", { term, per_state_limit: per });
      toast.success(`Yelp: ${data.inserted} new, ${data.updated} updated (${data.errors?.length || 0} errors)`, { id: "yelp" });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message, { id: "yelp" });
    }
  };

  const generateMock = async () => {
    const count = Number(prompt("How many demo leads? (1–500)", "50")) || 50;
    try {
      const { data } = await adminApi.post("/leads/generate-mock", { count });
      toast.success(`Demo: ${data.inserted} new leads generated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const importCsv = async (f) => {
    if (!f) return;
    const source = prompt("Source label? (e.g., yellowpages, apollo, leadspicker)", "yellowpages") || "csv";
    const form = new FormData();
    form.append("file", f);
    form.append("source", source);
    try {
      const { data } = await adminApi.post("/leads/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Parsed ${data.parsed}, inserted ${data.inserted}, updated ${data.updated}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await adminApi.delete(`/leads/${id}`);
    toast.success("Deleted");
    load();
  };

  const updateStatus = async (id, status) => {
    await adminApi.patch(`/leads/${id}`, { status });
    toast.success("Updated");
    load();
  };

  return (
    <div data-testid="admin-leads-page">
      <PageHeader
        eyebrow="Lead Finder"
        title="Prospect database"
        description="Search Yelp, import CSVs from Yellow Pages / Apollo / Leadspicker, or generate demo data."
        actions={
          <>
            <Btn variant="dark" onClick={yelpSearch} data-testid="leads-yelp-search">
              <MagnifyingGlass size={14} weight="bold" /> Yelp · all states
            </Btn>
            <Btn variant="ghost" onClick={() => fileRef.current?.click()} data-testid="leads-import-csv">
              <UploadSimple size={14} weight="bold" /> Import CSV
            </Btn>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              hidden
              onChange={(e) => importCsv(e.target.files?.[0])}
              data-testid="leads-csv-input"
            />
            <Btn variant="ghost" onClick={generateMock} data-testid="leads-generate-mock">
              <Sparkle size={14} weight="bold" /> Generate demo
            </Btn>
          </>
        }
      />
      <div className="p-8 space-y-6">
        {/* Filters */}
        <form
          onSubmit={search}
          className="bg-white border border-zinc-200 p-4 flex flex-wrap gap-3 items-end"
        >
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-1">Search</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="name, email, city…"
              className="w-full border border-zinc-300 p-2 text-sm focus:outline-none focus:border-[#0044FF]"
              data-testid="leads-q"
            />
          </div>
          <SelectInput label="State" value={state} onChange={setState} options={["", ...states]} testId="leads-state" />
          <SelectInput label="Source" value={source} onChange={setSource} options={["", "yelp", "csv", "mock"]} testId="leads-source" />
          <SelectInput label="Status" value={status} onChange={setStatus} options={["", "new", "contacted", "qualified", "rejected"]} testId="leads-status" />
          <Btn variant="primary" type="submit">Apply</Btn>
          <Btn variant="ghost" type="button" onClick={load} title="Refresh">
            <ArrowClockwise size={14} weight="bold" />
          </Btn>
        </form>

        {/* Table */}
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left">
              <tr className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                <th className="p-3">Name</th>
                <th className="p-3">Location</th>
                <th className="p-3">Phone</th>
                <th className="p-3">Email</th>
                <th className="p-3">Source</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-50" data-testid={`lead-row-${r.id}`}>
                  <td className="p-3 align-top">
                    <div className="font-medium text-zinc-900">{r.name}</div>
                    {r.website && (
                      <a href={r.website} target="_blank" rel="noreferrer" className="text-[11px] text-[#0044FF] hover:underline">
                        {r.website.replace(/^https?:\/\//, "").slice(0, 40)}
                      </a>
                    )}
                  </td>
                  <td className="p-3 text-zinc-700 align-top">{[r.city, r.state].filter(Boolean).join(", ")}</td>
                  <td className="p-3 text-zinc-700 font-mono text-xs align-top">{r.phone || "—"}</td>
                  <td className="p-3 text-zinc-700 font-mono text-xs align-top">{r.email || "—"}</td>
                  <td className="p-3 align-top">
                    <span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase bg-zinc-100 text-zinc-700">{r.source}</span>
                  </td>
                  <td className="p-3 align-top">
                    <select
                      value={r.status || "new"}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className="text-xs border border-zinc-300 p-1 bg-white"
                      data-testid={`lead-status-${r.id}`}
                    >
                      {["new", "contacted", "qualified", "rejected"].map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-3 text-right align-top">
                    <button
                      onClick={() => del(r.id)}
                      className="p-1 text-zinc-400 hover:text-red-600"
                      title="Delete"
                      data-testid={`lead-delete-${r.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {!rows.length && !loading && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-zinc-500 text-sm">
                    No leads yet. Run a Yelp search, import a CSV, or generate demo data.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="p-3 border-t border-zinc-200 flex items-center justify-between text-xs font-mono text-zinc-600">
            <span>{total} total · showing {rows.length}</span>
            <div className="flex gap-2">
              <button
                disabled={skip === 0}
                onClick={() => setSkip(Math.max(0, skip - 50))}
                className="px-3 py-1 border border-zinc-300 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                disabled={skip + rows.length >= total}
                onClick={() => setSkip(skip + 50)}
                className="px-3 py-1 border border-zinc-300 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectInput({ label, value, onChange, options, testId }) {
  return (
    <div className="min-w-[120px]">
      <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-zinc-300 p-2 text-sm bg-white"
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o} value={o}>{o || "All"}</option>
        ))}
      </select>
    </div>
  );
}
